import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import ModeSwitch from "@/components/feature/ModeSwitch";
import SavedSearchesMenu from "@/components/feature/SavedSearchesMenu";
import { useCompare } from "@/context/CompareContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useMode } from "@/context/ModeContext";
import { BRAND } from "@/lib/brand";

const menuLinks = [
  { href: "/search", label: "キーワード検索", icon: "ri-search-line" },
  { href: "/advanced-search", label: "詳細検索", icon: "ri-filter-3-line" },
  { href: "/ranking", label: "ランキング", icon: "ri-bar-chart-2-line" },
  { href: "/new", label: "新着作品", icon: "ri-sparkling-line" },
  { href: "/genres", label: "ジャンルから探す", icon: "ri-layout-grid-line" },
  { href: "/discover", label: "好み診断", icon: "ri-magic-line" },
  { href: "/compare", label: "作品比較", icon: "ri-scales-3-line" },
  { href: "/favorites", label: "お気に入り", icon: "ri-heart-line" },
  { href: "/watch-later", label: "あとで見る", icon: "ri-bookmark-line" },
  { href: "/articles", label: "特集記事", icon: "ri-article-line" },
  { href: "/about", label: "サイトについて", icon: "ri-information-line" },
  { href: "/settings", label: "設定・保存データ", icon: "ri-settings-3-line" },
];

const iconButtonClass =
  "flex h-11 w-11 items-center justify-center rounded-md text-lg text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  // 「保存した検索条件」は開いたときに初めて読み込む（メニューは軽いまま）
  const [menuView, setMenuView] = useState<"main" | "saved">("main");
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useMode();
  const { count: favoriteCount } = useFavorites();
  const { compareIds } = useCompare();

  useEffect(() => {
    if (!menuOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen((open) => {
      if (!open) setMenuView("main");
      return !open;
    });
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="page-container">
        <div className="flex h-14 items-center gap-3 md:h-[68px] md:gap-5">
          <BrandLogo />

          {/* PC: 検索へのアクセスを中央に常設（入力はトップ／検索ページで行う） */}
          <div className="hidden min-w-0 flex-1 justify-center md:flex">
            <Link
              to="/search"
              className="flex h-10 w-full max-w-[380px] items-center gap-2 rounded-lg border border-background-300 bg-background-100 px-3 text-[13px] text-foreground-500 transition-colors hover:border-primary-300 hover:bg-background-50 hover:text-foreground-700"
              aria-label="作品名・作者・出演者・ジャンルで検索"
            >
              <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
              <span className="truncate">作品名・作者・出演者・ジャンルで検索</span>
            </Link>
          </div>

          <div className="hidden shrink-0 md:block">
            <ModeSwitch />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-0.5 md:ml-0">
            <Link to="/search" aria-label="作品を検索" title="作品を検索" className={`${iconButtonClass} md:hidden`}>
              <i className="ri-search-line" aria-hidden="true" />
            </Link>
            <Link
              to="/favorites"
              aria-label={`お気に入り（${favoriteCount}件）`}
              title="お気に入り"
              className={`relative ${iconButtonClass}`}
            >
              <i className="ri-heart-line" aria-hidden="true" />
              {favoriteCount > 0 ? (
                <span className="absolute right-1 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-background-50">
                  {favoriteCount}
                </span>
              ) : null}
            </Link>
            <Link
              to="/compare"
              aria-label={`作品比較（${compareIds.length}件）`}
              title="作品比較"
              className={`relative hidden sm:flex ${iconButtonClass}`}
            >
              <i className="ri-scales-3-line" aria-hidden="true" />
              {compareIds.length > 0 ? (
                <span className="absolute right-1 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-foreground-950">
                  {compareIds.length}
                </span>
              ) : null}
            </Link>
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={toggleMenu}
                aria-expanded={menuOpen}
                aria-haspopup="true"
                aria-label="メニュー"
                className={iconButtonClass}
              >
                <i className={menuOpen ? "ri-close-line" : "ri-menu-3-line"} aria-hidden="true" />
              </button>
              {menuOpen ? (
                <div className="absolute right-0 top-full mt-2 w-[300px] max-w-[calc(100vw-2rem)] rounded-lg border border-background-200 bg-background-50 p-2">
                  {menuView === "saved" ? (
                    <SavedSearchesMenu
                      onClose={() => setMenuOpen(false)}
                      onBack={() => setMenuView("main")}
                    />
                  ) : (
                    <>
                      <nav aria-label="サイトメニュー">
                        <ul className="grid gap-0.5">
                          <li>
                            <button
                              type="button"
                              onClick={() => setMenuView("saved")}
                              className="flex min-h-[44px] w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground-800 transition-colors hover:bg-background-100"
                            >
                              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                                <i className="ri-bookmark-3-line" aria-hidden="true" />
                              </span>
                              保存した検索条件
                              <i className="ri-arrow-right-s-line ml-auto text-foreground-400" aria-hidden="true" />
                            </button>
                          </li>
                          {menuLinks.map((link) => (
                            <li key={link.href}>
                              <Link
                                to={link.href}
                                onClick={() => setMenuOpen(false)}
                                className="flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                              >
                                <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                                  <i className={link.icon} aria-hidden="true" />
                                </span>
                                {link.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </nav>
                      <div className="mt-2 border-t border-background-200 px-3 py-2 text-[11px] text-foreground-500">
                        表示中：{mode === "female" ? "女性向け" : "男性向け"}モード
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="pb-2 md:hidden">
          <ModeSwitch compact />
        </div>
      </div>
      <span className="sr-only">{BRAND.nameEn}</span>
    </header>
  );
}